import { Component, OnInit, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ResidentModel } from '@shared/models/data/resident-model';
import { CredentialModel } from '@shared/models/data/credential-model';
import { ResidentSearchModel } from '@shared/models/data/resident-search-model';
import { AuthService } from '@shared/services/auth.service';
import { FormValidationService } from '@shared/services/form-validation.service';
import { EnrollmentService } from '@shared/services/enrollment.service';
import { SessionService } from '@shared/services/session.service';
import { RosterService } from '@shared/services/roster.service';

@Component({
    selector: 'aaos-app-new-resident-dialog',
    templateUrl: './new-resident-dialog.component.html',
    styleUrls: ['./new-resident-dialog.component.scss']
})
export class NewResidentDialogComponent implements OnInit {

    newResident: ResidentModel;
    formGroup: FormGroup;
    firstNameControl = 'firstName';
    lastNameControl = 'lastName';
    emailControl = 'email';
    confirmEmailControl = 'confirmEmail';
    noninstitutionalEmailControl = 'noninstitutionalEmail';
    startDateControl = 'startDate';
    endDateControl = 'endDate';
    credentialCodeControl = 'credentialCode';
    editPrivileges = false;
    resident: ResidentModel;
    defaultStartDate: Date;
    defaultEndDate: Date;
    errors: Array<string>;
    loading = false;
    pgy: number;
    currentDate: Date;
    startDateReasons: string[];
    endDateReasons: string[];
    credentialList: Array<CredentialModel>;

    constructor(public newResidentDialogRef: MatDialogRef<NewResidentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ResidentSearchModel,
        private dialog: MatDialog,
        private authService: AuthService,
        private enrollmentService: EnrollmentService,
        private sessionService: SessionService,
        private rosterService: RosterService
    ) {
        newResidentDialogRef.addPanelClass('double-column-dialog');
    }

    ngOnInit() {
        this.loading = true;
        this.createFormGroup();

        this.credentialList = new Array<CredentialModel>();
        this.credentialList.push({credentialCode: '21', credentialDescription: 'Allopathic'});
      this.credentialList.push({ credentialCode: '22', credentialDescription: 'Osteopathic'});
        if (this.data != null) {
            this.resident = new ResidentModel();
            this.resident.id = this.data.id;
            this.resident.institutionId = this.sessionService.getInstitutionId();
            this.resident.firstName = this.data.firstName;
            this.resident.lastName = this.data.lastName;
            this.resident.institutionalEmailAddress = this.data.primaryEmailAddress;
            this.resident.nonInstitutionalEmailAddress = this.data.additionalEmailAddress;
            this.resident.credentialCode = this.data.defaultCredentialCode;
            this.formGroup.controls[this.firstNameControl].setValue(this.resident.firstName);
            this.formGroup.controls[this.lastNameControl].setValue(this.resident.lastName);
            this.formGroup.controls[this.emailControl].setValue(this.resident.institutionalEmailAddress);
            this.formGroup.controls[this.noninstitutionalEmailControl].setValue(this.resident.nonInstitutionalEmailAddress);
            this.formGroup.controls[this.credentialCodeControl].setValue(this.resident.credentialCode);

            this.enrollmentService.getCurrentDate(this.sessionService.getMcid())
                .subscribe(x => {
                    this.currentDate = new Date(x.currentDate);
                    this.currentDate = new Date(this.currentDate.getUTCFullYear(),
                                                this.currentDate.getUTCMonth(),
                                                this.currentDate.getUTCDate());

                    // Set default dates to July 1 and June 30. Please note month is 0-based when new dates are instantiated.
                    this.defaultStartDate = new Date(this.currentDate.getFullYear(), 6, 1);
                    this.defaultEndDate = new Date(this.currentDate.getFullYear() + 5, 5, 30);

                    this.resident.residencyBeginDate = new Date(this.defaultStartDate);
                    this.resident.residencyEndDate = new Date(this.defaultEndDate);

                    this.formGroup.controls[this.startDateControl].setValue(this.resident.residencyBeginDate);
                    this.formGroup.controls[this.endDateControl].setValue(this.resident.residencyEndDate);
                    this.updatePGY();
                    this.validateDates();
                    this.loading = false;
                });

        }

        this.editPrivileges = this.authService.changeAuthorized();
    }

    updatePGY() {
        this.pgy = this.rosterService.getPGY(new Date(this.currentDate), this.formGroup.controls[this.endDateControl].value);
    }

    invalidRequiredField(fieldName: string): boolean {
        if (this.formGroup.get(fieldName).hasError('required')) {
            return true;
        }
        return false;
    }

    validateDates() {
        this.startDateReasons = this.rosterService.isValidStartDate(
            new Date(this.formGroup.controls[this.startDateControl].value),
            new Date(this.formGroup.controls[this.endDateControl].value));
        this.endDateReasons = this.rosterService.isValidEndDate(new Date(
            this.formGroup.controls[this.startDateControl].value),
            new Date(this.formGroup.controls[this.endDateControl].value));
    }

    createFormGroup() {
    this.formGroup = new FormGroup({
            firstName: new FormControl('', [Validators.required]),
            lastName: new FormControl('', [Validators.required]),
            email: new FormControl({ value: '', disabled: this.data.primaryEmailAddress }),
            confirmEmail: new FormControl({ value: '', disabled: this.data.primaryEmailAddress }),
            noninstitutionalEmail: new FormControl('', [Validators.email]),
            startDate: new FormControl('', [Validators.required]),
            endDate: new FormControl('', [Validators.required]),
            credentialCode: new FormControl({value: this.data.defaultCredentialCode, disabled: false})
        });

        this.formGroup.controls['credentialCode'].setValidators([Validators.required]);
        this.formGroup.controls['email'].setValidators([Validators.required, Validators.email]);
        this.formGroup.controls['confirmEmail'].setValidators([Validators.required, Validators.email]);
        this.formGroup.setValidators(FormValidationService.getControlEmailValidator(
            this.formGroup.controls['email'], this.formGroup.controls['confirmEmail']));
    }

    closeDialog() {
        this.newResidentDialogRef.close();
    }

    onSubmit() {
        this.loading = true;

        this.newResident = {
            id: this.resident.id,
            institutionId: this.resident.institutionId,
            labelName: this.resident.labelName,
            sortName: this.resident.sortName,
            firstName: this.formGroup.controls[this.firstNameControl].value,
            lastName: this.formGroup.controls[this.lastNameControl].value,
            institutionalEmailAddress: this.formGroup.controls[this.emailControl].value,
            nonInstitutionalEmailAddress: this.formGroup.controls[this.noninstitutionalEmailControl].value,
            residencyBeginDate: this.formGroup.controls[this.startDateControl].value,
            residencyEndDate: this.formGroup.controls[this.endDateControl].value,
            canEditTransition: false,
            residencyPostGraduateYear: this.pgy.toString(),
            isTransitioned: false,
            credentialCode: this.formGroup.controls[this.credentialCodeControl].value,
            canEditCredentialCode: this.resident.canEditCredentialCode,
            npi: ''
        };

        this.enrollmentService.saveResident(this.newResident).subscribe(results => {
            this.loading = false;

            // Clear errors
            this.errors = new Array<string>();

            if (results.isValid) {
                this.enrollmentService.notifyOfResidentEnrollment(results.resident).subscribe();
                this.newResidentDialogRef.close();
            } else {
                this.errors = results.errors;
            }
        });
    }

    getModalConfig() {
        return {
            panelClass: 'wide-dialog',
            disableClose: true,
            autoFocus: false,
            data: null,
        } as MatDialogConfig;
    }

    public canAddResident(): boolean {
        return this.formGroup.valid && this.editPrivileges && this.startDateReasons.length === 0 && this.endDateReasons.length === 0;
    }

}
